import dataManager from 'pages/main/data/DataManager'

import { textToHTML, getCid } from 'utils'

import { isRescuedInlineAttachment } from './rescuedInlineAttachment'
import ICAL from 'ical.js'

import { getMeetingInfo } from './IcsParser'
import ICSParser from './IcsParser'

import { shouldShowDraftBadge } from './draftBadge'

import { JMAPEmail } from 'pages/main/data/JMAPMailbox'
import { ScheduleEmail } from 'pages/main/data/ScheduleMailbox'
import { EMLEmail } from 'pages/main/data/EMLEmail'

import attachmentHelper from './attachmentHelper'
import { extendUI } from 'pages/main/components/Attachment/helperUI'

import { errorBase } from 'utils/ui'

import { usePrint, useLocalThreadViewer } from 'vars'

// Type definitions
interface EmailAddress {
  email: string
  name?: string
}

interface Label {
  id: number
  name: string
  background_color?: string
  text_color?: string
}

export interface ThreadInfo {
  labels: Label[]
  important: boolean
}

interface Attendee {
  name?: string | null
  email?: string | null
}

interface EventInfo {
  start_date: string
  end_date?: string
  all_day: boolean
  location?: string
  attendees: Attendee[]
  title: string
  description?: string | null
  meeting_link?: string
  status?: string
  id?: string
}

interface TaskInfo {
  id: string
  due_date: string
  complete_date: string
  task_status: string
  is_assigned_me?: boolean
  assignee: Attendee[]
  subject: string
  body: string
}

interface InfoHistory {
  value: string
  date?: string | null
}

interface InfoDetail {
  key: string
  value?: string
  histories?: InfoHistory[]
}

export interface AIExtraction {
  events: EventInfo[]
  tasks: TaskInfo[]
  infos: InfoDetail[]
}

interface BodyProp {
  type: string
  body: string
  attachments: any[]
  calendar?: string
}

interface BodyData {
  type: string
  body: string
  attachments: any[]
  meeting_info?: any
}

interface EmailInfoData {
  id: string
  messageId: string
  from: EmailAddress
  to: EmailAddress[]
  cc: EmailAddress[]
  bcc: EmailAddress[]
  subject: string
  reply_to: EmailAddress[]
  received_time: number
  flagged: boolean
  read: boolean
  draft: boolean
  summary: string | null
  priority: string
  sensitivity: string
  priority_inbox?: string
  has_attachment: boolean
  has_ai_extraction: boolean
  enable_print: boolean
  isLoaded?: boolean
  is_scheduled_email?: boolean
  enable_flag?: boolean
  enable_read?: boolean
  enable_delete?: boolean
  enable_reply_forward?: boolean
  ai_preview?: string
  quoted_visible?: boolean
  ai_extraction?: AIExtraction
  ai_extraction_feedback?: boolean | null
  // thread 보기에서 현재 메일함과 다른 메일함에 속한 메일의 메일함 표시 이름.
  // 단일 메일 보기(비 thread 모드)에서는 전달하지 않는다.
  mailbox_name?: string
  // mailbox_name 의 전체 경로명(부모/자식). 뷰어에서 hover tooltip 으로 표시한다.
  mailbox_fullname?: string
  // parsed-body-doc-from-viewer(Gate B·정책): 뷰어가 본문 표시 파싱의 원본 보존 clone 을
  // host 에 캡처 보고해도 되는 메일인지. 미지정=false — 편집/회신 재사용이 성립하는
  // JMAPEmail(+draft 인 ScheduleEmail)만 true 로 보낸다. EML·외부 host(om-platform 등
  // 플래그 미전송)는 자동으로 캡처가 꺼진다. 크기 실익(≥256KB)은 뷰어가 판단한다.
  enable_capture_element?: boolean
}

interface EmailAPI {
  hasAiExtraction: () => boolean
  getAttachment: (attachmentId: string) => any
  setFlagged: (value: boolean) => void
  setRead: (value: boolean) => void
  getBody: () => Promise<BodyData | null>
}

interface EmailInfo {
  email: JMAPEmail | ScheduleEmail | EMLEmail
  attachmentMap: { [key: string]: any }
  dataForJSON: EmailInfoData
  generateData: () => void
  getMeetingInfo: () => { body: BodyData } | null
  api: EmailAPI
  body?: BodyProp
  bodyData?: BodyData
  meeting_info?: any
  detail?: any
  inlineFiles?: any[]
  icsParser?: any
}

const getEmailList = (emailIds: string[]) => {
  return emailIds
    .map((emailId) => {
      return dataManager.getEmailById(emailId)
    })
    .filter((email) => email != null)
}

const setFlag = (emailIds: string[], value: boolean, targetMailbox: any): Promise<any> => {
  const promise = new Promise((resolve) => {
    console.log(' --- setFlag : ', emailIds, value)
    setTimeout(() => {
      const emailList = getEmailList(emailIds)
      dataManager.setKeyword(emailList, '$flagged', value, targetMailbox).then((ret) => {
        resolve(ret)
      })
    }, 10)
  })

  return promise
}

const setRead = (emailIds: string[], value: boolean, targetMailbox: any): Promise<any> => {
  const promise = new Promise((resolve) => {
    console.log(' --- setRead : ', emailIds, value)
    setTimeout(() => {
      const emailList = getEmailList(emailIds)
      if (value === false || emailList.length > 1) {
        dataManager.setKeyword(emailList, '$seen', value, targetMailbox).then((ret) => {
          resolve(ret)
        })
      } else {
        dataManager.checkOpenUnread(emailList[0], targetMailbox).then((ret) => {
          resolve(ret)
        })
      }
    }, 10)
  })

  return promise
}

// const getMessageId = (email) => {
//   if (email.data.headers != null) {
//     const messageIdHeader = email.data.headers.find((header) => {
//       if (header.name.toLowerCase() === 'message-id') {
//         return true
//       }
//     })

//     const messageId = messageIdHeader.value.replace(/(^\s*<)|(>\s*$)/g, '')
//     return messageId
//   }
//   return null
// }

let _threadInfoCache: ThreadInfo | null = null
let _aiExtractionCache: AIExtraction | null = null

export const loadThreadInfo = async (): Promise<ThreadInfo> => {
  if (useLocalThreadViewer) {
    if (_threadInfoCache != null) return _threadInfoCache
    const mod = await import('./__fixtures__/threadInfo')
    _threadInfoCache = mod.default
  }
  return _threadInfoCache
}

export const loadAiExtractionSample = async (): Promise<AIExtraction> => {
  if (useLocalThreadViewer) {
    if (_aiExtractionCache != null) return _aiExtractionCache
    const mod = await import('./__fixtures__/aiExtractionSample')
    _aiExtractionCache = mod.default
  }
  return _aiExtractionCache
}

if (__ENV_LOCAL__) {
  loadThreadInfo()
  loadAiExtractionSample()
}

// copedEmail 정보는 실제 이메일 정보를 복사해서 만들어진다.
// Api는 존재하지 않고, data만 가진 상태로 사용된다.
// draft 정보가 변경되더라도 최초의 draft ID를 유지해서 사용하기 위해, 최초의 draft ID가 적용된 copedEmail이 전달된다.
function JMAPEmailInfo(
  this: EmailInfo,
  copedEmail: JMAPEmail,
  isOutbox: boolean,
  targetMailbox: any,
  isThreadMode: boolean,
): void {
  const self = this

  const email = copedEmail

  self.email = copedEmail
  self.attachmentMap = {}

  const pInboxes = ['important', 'news', 'company', 'other']

  const emailObj = dataManager.getEmailById(email.data.id)

  let data: EmailInfoData = {} as EmailInfoData

  self.generateData = () => {
    if (emailObj == null) {
      console.warn('[JMAPEmailInfo] emailObj not found for id:', email.data.id)
      return
    }
    // 발송이 시작된 메일은 곧 sent가 되므로 viewer에 draft([초안])로 그리지 않는다.
    // (발송 직전 forceSave가 만든 draft 상태가 지연된 setThread로 [초안]이 렌더되는 문제 차단.
    //  발송 시작 시 SendHelper가 dataMgr.sendingEmailIds에 id를 등록한다.)
    // 결정 로직은 draftBadge.shouldShowDraftBadge로 분리(결정론적 테스트 대상).
    const sendingSet: Set<string> | undefined = emailObj.dataMgr?.sendingEmailIds
    const isSendingNow =
      sendingSet != null && emailObj.data != null && sendingSet.has(emailObj.data.id)
    const isEditable = shouldShowDraftBadge(emailObj.isDraft() === true, isOutbox, isSendingNow)

    const from =
      email.data.from != null && email.data.from.length > 0 ? email.data.from[0] : { email: ' ' }

    // thread 보기에서만, 현재 메일함(targetMailbox)이 담고 있지 않은(= 다른 메일함에 속한)
    // 메일에 대해 메일함 표시 이름을 계산한다. ThreadList item 과 동일한 판별 로직.
    // 단일 메일 보기(isThreadMode !== true)에서는 전달하지 않는다.
    let mailboxName: string | undefined
    let mailboxFullName: string | undefined
    if (
      isThreadMode === true &&
      targetMailbox != null &&
      (targetMailbox.isRealMailbox !== true ||
        targetMailbox.isfolderNameVisible(emailObj) === true) &&
      emailObj.data.mailboxIds != null
    ) {
      const mailboxIdList = Object.keys(emailObj.data.mailboxIds)
      if (mailboxIdList.length > 0) {
        const otherMailbox = dataManager.getMailboxById(mailboxIdList[0], true)
        if (otherMailbox != null) {
          mailboxName = otherMailbox.getDisplayName()
          mailboxFullName = otherMailbox.getFullName()
        }
      }
    }

    data = {
      id: email.data.id,
      // 이메일마다 고유한 값을 지정하면 모든 메일이 표시됨. (중복되는 messageId는 숨김 처리.)
      messageId: email.data.id, // getMessageId(email),
      from,
      to: [...(email.data.to || [])],
      cc: [...(email.data.cc || [])],
      bcc: [...(email.data.bcc || [])],
      subject: emailObj.getViewSubject(),
      reply_to: [...(email.data.replyTo || [])],
      received_time: new Date(emailObj.getViewTime()).getTime() / 1000,
      flagged: email.isFlagged(),
      read: email.isRead(),
      draft: isEditable,
      summary: emailObj.getPreview(),
      priority: 'normal',
      sensitivity: 'normal',
      // priority_inbox: pInboxes[randomIntFromInterval(0, pInboxes.length - 1)],
      has_attachment: emailObj.hasAttachment(),
      // 현재 이메일의 상태 표시.
      // In-progress, Completed, Requested, Overdue
      // ai_email_status: 'Requested',
      has_ai_extraction: false, // true, false - false이면 요청을 할 수 없음.
      enable_print: usePrint,
      isLoaded: email.isLoaded,
      mailbox_name: mailboxName,
      mailbox_fullname: mailboxFullName,
      // JMAP 메일은 draft 여부와 무관하게 캡처 허용 — 편집(draft)·reply/forward·resend 가
      // 같은 본문 문자열을 재사용한다(소비 시 원문 === 검증).
      enable_capture_element: true,
    }

    if (__ENV_LOCAL__) {
      if (useLocalThreadViewer) {
        function randomIntFromInterval(min: number, max: number): number {
          // min and max included
          return Math.floor(Math.random() * (max - min + 1) + min)
        }

        data.priority_inbox = pInboxes[randomIntFromInterval(0, pInboxes.length - 1)]
        data.has_ai_extraction = isEditable !== true
        const preview = emailObj.getPreview()
        data = {
          ...data,
          summary:
            preview != null
              ? `${preview} - summary~~!!!  summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!! summary~~!!!`
              : null,
          // meeting_info: {}, // getBody에서 처리..
          ai_preview: 'This is AI preview...!!!',
          quoted_visible: false,
          ai_extraction: _aiExtractionCache,
          ai_extraction_feedback: null, // true, false, null 전달 가능.
        }
      }
    }

    self.dataForJSON = data
  }

  self.generateData()

  // const tmpHTML = ``

  const getBodyData = (bodyProp: BodyProp): BodyData => {
    const jsonData: BodyData = {
      type: bodyProp.type,
      body: bodyProp.body,
      attachments: bodyProp.attachments.map((attachment) => {
        return attachment.dataForJSON
      }),
    }

    // jsonData.body = tmpHTML

    const extData = emailObj.getExtData()
    if (extData.calendarEvent != null) {
      const { meeting_info } = extData.calendarEvent
      if (meeting_info != null) {
        // officemail에 추가한 초대 메일에 outcome 정보가 있으면
        // sequence 값을 무시하고 outcome 정보에 맞게 설정.
        // 없는 경우 Meeting에 있는 sequence 값을 사용.
        if (emailObj.isOutcomeAdded()) {
          meeting_info.sequence = 0
        } else if (emailObj.isOutcomeUpdated()) {
          meeting_info.sequence = 1
        }
      }
      self.bodyData = { ...jsonData }
      self.meeting_info = meeting_info
      jsonData.meeting_info = meeting_info
    }

    // jsonData.body = ``

    return jsonData
  }

  self.getMeetingInfo = () => {
    const extData = emailObj.getExtData()
    if (extData.calendarEvent != null) {
      const jsonData: BodyData = {
        ...self.bodyData!,
      }
      const { meeting_info } = extData.calendarEvent
      self.meeting_info = meeting_info
      jsonData.meeting_info = meeting_info

      return {
        body: jsonData,
      }
    }

    return null
  }

  self.api = {
    hasAiExtraction: () => {
      return true
    },
    getAttachment: (attachmentId: string) => {
      return self.attachmentMap[attachmentId]
    },
    setFlagged: (value: boolean) => {
      setFlag([data.id], value, targetMailbox)
    },
    setRead: (value: boolean) => {
      setRead([data.id], value, targetMailbox)
    },
    getBody: (): Promise<BodyData> => {
      const promise = new Promise<BodyData>((resolve) => {
        if (self.body == null) {
          const email = dataManager.getEmailById(data.id)

          email
            .reqDetail()
            .then((detail: any) => {
              console.log(' -- mail detail : ', detail)
              self.detail = detail

              let type: string | null = null
              let body = ''
              let calendar: string | null = null
              const { bodyPart } = detail
              if (bodyPart != null) {
                if (detail.bodyPart.isCalendar() === true) {
                  type = 'text/calendar'
                  calendar = detail.bodyPart.getCalendarBody()
                  if (detail.bodyPart.hasHTMLBody() === true) {
                    body = detail.bodyPart.getHTMLBody()
                  }
                } else if (detail.bodyPart.hasHTMLBody() === true) {
                  type = 'text/html'
                  body = detail.bodyPart.getHTMLBody()
                } else if (detail.bodyPart.hasTextBody() === true) {
                  // type = 'text/plain'
                  body = detail.bodyPart.getTextBody()
                  // Pure text를 html로 변환.
                  type = 'text/html'
                  body = textToHTML(body)
                } else {
                  type = 'text/html'
                  body = ''
                }
              } else {
                type = 'text/html'
                body = ''
              }

              const attachments: any[] = []
              const deferredAttachments: any[] = []
              const inlineFiles: any[] = []

              if (bodyPart != null && bodyPart.files != null) {
                const isHTML = detail.bodyPart.hasHTMLBody() === true

                bodyPart.files.forEach((file: any, idx: number) => {
                  const { cid, contentId, content } = file
                  // inline 이미지 검사 방식 변경.
                  // let isInline = (isHTML && file.disposition !== 'attachment')
                  let isInline = isHTML === true && (cid != null || contentId != null)
                  if (isInline === true) {
                    const cid0 = getCid(cid || contentId)
                    // cid가 존재하면 항상 inline 이미지로 처리.
                    isInline = body.indexOf(cid0) > -1
                  }

                  if (isInline === true) {
                    file.isInline = true

                    const cid0 = getCid(cid || contentId)
                    let inlineFile0 = null
                    if (content == null) {
                      inlineFile0 = attachmentHelper.createAttachment(file, cid0, idx)
                    } else {
                      // eml의 inline 이미지 처리.
                      inlineFile0 = attachmentHelper.createAttachmentForEML(file, cid0, idx)
                    }
                    const inlineFile = extendUI(inlineFile0)
                    inlineFiles.push(inlineFile)
                  } else {
                    // cid가 있지만 본문에서 참조되지 않는 이미지(Apple Mail 등)는
                    // nonInlineAttachments(cid 보유 파일은 제외됨)에 없어도 일반 첨부파일로
                    // 표시한다. 숨기면 이미지 내용을 볼 방법이 없다. (Gmail/Outlook 동일 동작)
                    const isRescuedInline = isRescuedInlineAttachment(bodyPart, file)
                    // 그 외에는 nonInlineAttachments에 없으면 표시하지 않는다.
                    const existFile =
                      isRescuedInline === true
                        ? file
                        : email.nonInlineAttachments.find((attachment: any) => {
                            return attachment.blobId === file.blobId
                          })
                    if (existFile != null) {
                      let attachment0 = null
                      if (content == null) {
                        attachment0 = attachmentHelper.createAttachment(file, null, idx)
                      } else {
                        // eml의 첨부 파일 처리.
                        attachment0 = attachmentHelper.createAttachmentForEML(file, null, idx)
                      }
                      const attachmentObj = extendUI(attachment0)

                      self.attachmentMap[attachmentObj.id] = attachmentObj

                      if (isRescuedInline === true) {
                        // 비정상 데이터로 구제된 인라인 이미지는 정상 첨부파일 뒤에 표시한다.
                        deferredAttachments.push(attachmentObj)
                      } else {
                        attachments.push(attachmentObj)
                      }
                    }
                  }
                })

                attachments.push(...deferredAttachments)
              }

              self.inlineFiles = inlineFiles

              // body = ``

              self.body = {
                type: type!,
                body,
                attachments,
                // inline_images: inlineFiles,
              }

              if (calendar != null) {
                self.body.calendar = calendar
              }

              const bodyData = getBodyData(self.body)
              resolve(bodyData)
            })
            .catch((err: any) => {
              console.error(' -- getBody error : ', err)
              // self.body를 캐시하지 않고 null로 둬서(다음 unfold 시 self.body == null 경로로)
              // reqDetail을 재시도하게 한다. 빈 body를 캐시하면 서버 복구 후에도 빈 화면 잔류.
              resolve(getBodyData({ type: 'text/html', body: '', attachments: [] }))
            })
        } else {
          const bodyData = getBodyData(self.body)
          resolve(bodyData)
        }
      })

      return promise
    },
    // getInlineImagesData: () => {
    //   const inlineImages = self.inlineFiles.map((attachment) => {
    //     return attachment.dataForJSON
    //   })
    //   return JSON.stringify(inlineImages)
    // },
  }
}

function ScheduleEmailInfo(
  this: EmailInfo,
  copedEmail: ScheduleEmail,
  isOutbox: boolean,
  targetMailbox: any,
): void {
  const self = this

  const email = copedEmail

  self.email = copedEmail
  self.attachmentMap = {}

  let data: EmailInfoData = {} as EmailInfoData

  self.generateData = () => {
    // const pInboxes = ['important', 'news', 'company', 'other']

    const emailObj = targetMailbox.getEmailById(email.data.id)

    if (emailObj == null) {
      console.warn('[ScheduleEmailInfo] emailObj not found for id:', email.data.id)
      return
    }
    const isEditable = emailObj.isDraft() === true || isOutbox

    const from =
      email.data.from != null && email.data.from.length > 0 ? email.data.from[0] : { email: ' ' }

    const { hasAttachment, attachments } = email.data
    let hasAttachmentNotInline =
      hasAttachment === true &&
      attachments.some((attachment: any) => attachment.disposition === 'attachment')

    data = {
      id: email.data.id,
      // 이메일마다 고유한 값을 지정하면 모든 메일이 표시됨. (중복되는 messageId는 숨김 처리.)
      messageId: email.data.id, // getMessageId(email),
      from,
      to: [...(email.data.to || [])],
      cc: [...(email.data.cc || [])],
      bcc: [...(email.data.bcc || [])],
      subject: email.data.subject,
      reply_to: [...(email.data.replyTo || [])],
      received_time: new Date(emailObj.getViewTime()).getTime() / 1000,
      flagged: email.isFlagged(),
      read: email.isRead(),
      draft: isEditable,
      summary: emailObj.getPreview(),
      priority: 'normal',
      sensitivity: 'normal',
      // priority_inbox: pInboxes[randomIntFromInterval(0, pInboxes.length - 1)],
      has_attachment: hasAttachmentNotInline,
      // 현재 이메일의 상태 표시.
      // In-progress, Completed, Requested, Overdue
      // ai_email_status: 'Requested',
      has_ai_extraction: false, // true, false - false이면 요청을 할 수 없음.
      enable_print: usePrint,
      is_scheduled_email: true,
      // 예약 메일은 draft(편집 가능) 상태일 때만 캡처 허용 — 그 외엔 재사용 소비처가 없다.
      enable_capture_element: isEditable,
    }

    self.dataForJSON = data
  }

  self.generateData()

  // const tmpHTML = ``

  const getBodyData = (bodyProp: BodyProp): BodyData => {
    const jsonData: BodyData = {
      type: bodyProp.type,
      body: bodyProp.body,
      attachments: bodyProp.attachments.map((attachment) => {
        return attachment.dataForJSON
      }),
    }

    // jsonData.body = tmpHTML

    if (bodyProp.calendar != null) {
      let ics = bodyProp.calendar

      let meeting_info = null

      if (ics != null) {
        console.log(' --- ics : ', ics)

        try {
          const icsObj = ICAL.parse(ics)
          console.log(' --- icsObj : ', icsObj)

          self.icsParser = new (ICSParser as any)(icsObj)

          meeting_info = self.icsParser.getMeetingInfo({
            from: data.from,
            userInfo: dataManager.getUserInfo(),
          })

          console.log(' --- meeting_info : ', meeting_info)
        } catch (err) {
          console.log(' --- error : ', err)

          // errorBase({
          //   title: 'Error',
          //   content: i18n.t('meeting.error.icalParsingError'),
          // })
        }

        self.bodyData = { ...jsonData }
        self.meeting_info = meeting_info
        jsonData.meeting_info = meeting_info
      }
    }

    // jsonData.body = ``

    return jsonData
  }

  self.getMeetingInfo = () => {
    const bodyProp = self.body
    if (bodyProp != null && bodyProp.calendar != null) {
      const ics = bodyProp.calendar

      const jsonData: BodyData = {
        ...self.bodyData!,
      }
      const meeting_info = getMeetingInfo(ics, data.from)
      self.meeting_info = meeting_info
      jsonData.meeting_info = meeting_info

      return {
        body: jsonData,
      }
    }

    return null
  }

  self.api = {
    hasAiExtraction: () => {
      return false
    },
    getAttachment: (attachmentId: string) => {
      return self.attachmentMap[attachmentId]
    },
    setFlagged: (value: boolean) => {
      setFlag([data.id], value, targetMailbox)
    },
    setRead: (value: boolean) => {
      setRead([data.id], value, targetMailbox)
    },
    getBody: (): Promise<BodyData | null> => {
      const promise = new Promise<BodyData | null>((resolve) => {
        if (self.body == null) {
          const email = dataManager.getEmailById(data.id)

          email
            .reqDetail()
            .then((detail: any) => {
              console.log(' -- mail detail : ', detail)
              self.detail = detail

              let type: string | null = null
              let body = ''
              let calendar: string | null = null
              if (detail.bodyPart.isCalendar() === true) {
                type = 'text/calendar'
                calendar = detail.bodyPart.getCalendarBody()
                if (detail.bodyPart.hasHTMLBody() === true) {
                  body = detail.bodyPart.getHTMLBody()
                }
              } else if (detail.bodyPart.hasHTMLBody() === true) {
                type = 'text/html'
                body = detail.bodyPart.getHTMLBody()
              } else if (detail.bodyPart.hasTextBody() === true) {
                // type = 'text/plain'
                body = detail.bodyPart.getTextBody()
                // Pure text를 html로 변환.
                type = 'text/html'
                body = textToHTML(body)
              } else {
                type = 'text/html'
                body = ''
              }

              const { bodyPart } = detail

              const attachments: any[] = []
              const deferredAttachments: any[] = []
              const inlineFiles: any[] = []

              if (bodyPart.files != null) {
                const isHTML = detail.bodyPart.hasHTMLBody() === true

                bodyPart.files.forEach((file: any, idx: number) => {
                  const { cid, contentId, content } = file
                  // inline 이미지 검사 방식 변경.
                  // let isInline = (isHTML && file.disposition !== 'attachment')
                  let isInline = isHTML === true && (cid != null || contentId != null)
                  if (isInline === true) {
                    const cid0 = getCid(cid || contentId)
                    // cid가 존재하면 항상 inline 이미지로 처리.
                    isInline = body.indexOf(cid0) > -1
                  }

                  if (isInline === true) {
                    file.isInline = true

                    const cid0 = getCid(cid || contentId)
                    let inlineFile0 = null
                    if (content == null) {
                      inlineFile0 = attachmentHelper.createAttachment(file, cid0, idx)
                    } else {
                      // eml의 inline 이미지 처리.
                      inlineFile0 = attachmentHelper.createAttachmentForEML(file, cid0, idx)
                    }
                    const inlineFile = extendUI(inlineFile0)
                    inlineFiles.push(inlineFile)
                  } else {
                    let attachment0 = null
                    if (content == null) {
                      attachment0 = attachmentHelper.createAttachment(file, null, idx)
                    } else {
                      // eml의 첨부 파일 처리.
                      attachment0 = attachmentHelper.createAttachmentForEML(file, null, idx)
                    }
                    const attachmentObj = extendUI(attachment0)

                    self.attachmentMap[attachmentObj.id] = attachmentObj

                    if (isRescuedInlineAttachment(bodyPart, file) === true) {
                      // 비정상 데이터로 구제된 인라인 이미지는 정상 첨부파일 뒤에 표시한다.
                      deferredAttachments.push(attachmentObj)
                    } else {
                      attachments.push(attachmentObj)
                    }
                  }
                })

                attachments.push(...deferredAttachments)
              }

              self.inlineFiles = inlineFiles

              // body = ``

              self.body = {
                type: type!,
                body,
                attachments,
                // inline_images: inlineFiles,
              }

              if (calendar != null) {
                self.body.calendar = calendar
              }

              const bodyData = getBodyData(self.body)
              resolve(bodyData)
            })
            .catch((err: any) => {
              if (err.code != null) {
                errorBase({
                  title: 'Error',
                  content: err.message,
                })
              }
              // self.body를 캐시하지 않아(null 유지) 다음 unfold 시 reqDetail을 재시도한다.
              console.log(' -- getBody error : ', err)
              resolve(null)
            })
        } else {
          const bodyData = getBodyData(self.body)
          resolve(bodyData)
        }
      })

      return promise
    },
    // getInlineImagesData: () => {
    //   const inlineImages = self.inlineFiles.map((attachment) => {
    //     return attachment.dataForJSON
    //   })
    //   return JSON.stringify(inlineImages)
    // },
  }
}

function EMLEmailInfo(
  this: EmailInfo,
  copedEmail: EMLEmail,
  isOutbox: boolean /* , targetMailbox = null */,
): void {
  const self = this

  const email = copedEmail

  self.email = copedEmail
  self.attachmentMap = {}

  let data: EmailInfoData = {} as EmailInfoData

  self.generateData = () => {
    // const pInboxes = ['important', 'news', 'company', 'other']

    const emailObj = self.email

    if (emailObj == null) {
      console.warn('[EMLEmailInfo] emailObj is null')
      return
    }
    const isEditable = emailObj.isDraft() === true || isOutbox

    const from =
      email.data.from != null && email.data.from.length > 0 ? email.data.from[0] : { email: ' ' }

    const { hasAttachment, attachments } = email.data
    let hasAttachmentNotInline =
      hasAttachment === true &&
      attachments.some((attachment: any) => attachment.disposition === 'attachment')

    data = {
      id: email.data.id,
      // 이메일마다 고유한 값을 지정하면 모든 메일이 표시됨. (중복되는 messageId는 숨김 처리.)
      messageId: email.data.id, // getMessageId(email),
      from,
      to: [...(email.data.to || [])],
      cc: [...(email.data.cc || [])],
      bcc: [...(email.data.bcc || [])],
      subject: email.data.subject,
      reply_to: [...(email.data.replyTo || [])],
      received_time: new Date(emailObj.getViewTime()).getTime() / 1000,
      flagged: email.isFlagged(),
      read: email.isRead(),
      draft: isEditable,
      summary: emailObj.getPreview(),
      priority: 'normal',
      sensitivity: 'normal',
      // priority_inbox: pInboxes[randomIntFromInterval(0, pInboxes.length - 1)],
      has_attachment: hasAttachmentNotInline,
      // 현재 이메일의 상태 표시.
      // In-progress, Completed, Requested, Overdue
      // ai_email_status: 'Requested',
      has_ai_extraction: false, // true, false - false이면 요청을 할 수 없음.
      enable_print: usePrint,
      enable_flag: false,
      enable_read: false,
      enable_delete: false,
      enable_reply_forward: false,
    }

    self.dataForJSON = data
  }

  self.generateData()

  // const tmpHTML = ``

  const getBodyData = (bodyProp: BodyProp): BodyData => {
    const jsonData: BodyData = {
      type: bodyProp.type,
      body: bodyProp.body,
      attachments: bodyProp.attachments.map((attachment) => {
        return attachment.dataForJSON
      }),
    }

    // jsonData.body = tmpHTML

    if (bodyProp.calendar != null) {
      let ics = bodyProp.calendar

      let meeting_info = null

      if (ics != null) {
        console.log(' --- ics : ', ics)

        try {
          const icsObj = ICAL.parse(ics)
          console.log(' --- icsObj : ', icsObj)

          self.icsParser = new (ICSParser as any)(icsObj)

          meeting_info = self.icsParser.getMeetingInfo({
            from: data.from,
            userInfo: dataManager.getUserInfo(),
          })

          console.log(' --- meeting_info : ', meeting_info)
        } catch (err) {
          console.log(' --- error : ', err)

          // errorBase({
          //   title: 'Error',
          //   content: i18n.t('meeting.error.icalParsingError'),
          // })
        }

        self.bodyData = { ...jsonData }
        self.meeting_info = meeting_info
        jsonData.meeting_info = meeting_info
      }
    }

    // jsonData.body = ``

    return jsonData
  }

  self.getMeetingInfo = () => {
    const bodyProp = self.body
    if (bodyProp != null && bodyProp.calendar != null) {
      const ics = bodyProp.calendar

      const jsonData: BodyData = {
        ...self.bodyData!,
      }
      const meeting_info = getMeetingInfo(ics, data.from)
      self.meeting_info = meeting_info
      jsonData.meeting_info = meeting_info

      return {
        body: jsonData,
      }
    }

    return null
  }

  self.api = {
    hasAiExtraction: () => {
      return false
    },
    getAttachment: (attachmentId: string) => {
      return self.attachmentMap[attachmentId]
    },
    setFlagged: (/* value: boolean */) => {
      // do nothing
    },
    setRead: (/* value: boolean */) => {
      // do nothing
    },
    getBody: (): Promise<BodyData> => {
      const promise = new Promise<BodyData>((resolve) => {
        if (self.body == null) {
          const email = self.email

          email
            .reqDetail()
            .then((detail: any) => {
              console.log(' -- mail detail : ', detail)
              self.detail = detail

              let type: string | null = null
              let body = ''
              let calendar: string | null = null
              if (detail.bodyPart.isCalendar() === true) {
                type = 'text/calendar'
                calendar = detail.bodyPart.getCalendarBody()
                if (detail.bodyPart.hasHTMLBody() === true) {
                  body = detail.bodyPart.getHTMLBody()
                }
              } else if (detail.bodyPart.hasHTMLBody() === true) {
                type = 'text/html'
                body = detail.bodyPart.getHTMLBody()
              } else if (detail.bodyPart.hasTextBody() === true) {
                // type = 'text/plain'
                body = detail.bodyPart.getTextBody()
                // Pure text를 html로 변환.
                type = 'text/html'
                body = textToHTML(body)
              } else {
                type = 'text/html'
                body = ''
              }

              const { bodyPart } = detail

              const attachments: any[] = []
              const deferredAttachments: any[] = []
              const inlineFiles: any[] = []

              if (bodyPart.files != null) {
                const isHTML = detail.bodyPart.hasHTMLBody() === true

                bodyPart.files.forEach((file: any, idx: number) => {
                  const { cid, contentId, content } = file
                  // inline 이미지 검사 방식 변경.
                  // let isInline = (isHTML && file.disposition !== 'attachment')
                  let isInline = isHTML === true && (cid != null || contentId != null)
                  if (isInline === true) {
                    const cid0 = getCid(cid || contentId)
                    // cid가 존재하면 항상 inline 이미지로 처리.
                    isInline = body.indexOf(cid0) > -1
                  }

                  if (isInline === true) {
                    file.isInline = true

                    const cid0 = getCid(cid || contentId)
                    let inlineFile0 = null
                    if (content == null) {
                      inlineFile0 = attachmentHelper.createAttachment(file, cid0, idx)
                    } else {
                      // eml의 inline 이미지 처리.
                      inlineFile0 = attachmentHelper.createAttachmentForEML(file, cid0, idx)
                    }
                    const inlineFile = extendUI(inlineFile0)
                    inlineFiles.push(inlineFile)
                  } else {
                    let attachment0 = null
                    if (content == null) {
                      attachment0 = attachmentHelper.createAttachment(file, null, idx)
                    } else {
                      // eml의 첨부 파일 처리.
                      attachment0 = attachmentHelper.createAttachmentForEML(file, null, idx)
                    }
                    const attachmentObj = extendUI(attachment0)

                    self.attachmentMap[attachmentObj.id] = attachmentObj

                    if (isRescuedInlineAttachment(bodyPart, file) === true) {
                      // 비정상 데이터로 구제된 인라인 이미지는 정상 첨부파일 뒤에 표시한다.
                      deferredAttachments.push(attachmentObj)
                    } else {
                      attachments.push(attachmentObj)
                    }
                  }
                })

                attachments.push(...deferredAttachments)
              }

              self.inlineFiles = inlineFiles

              // body = ``

              self.body = {
                type: type!,
                body,
                attachments,
                // inline_images: inlineFiles,
              }

              if (calendar != null) {
                self.body.calendar = calendar
              }

              const bodyData = getBodyData(self.body)
              resolve(bodyData)
            })
            .catch((err: any) => {
              console.error(' -- getBody error : ', err)
              // self.body를 캐시하지 않고 null로 둬서(다음 unfold 시 self.body == null 경로로)
              // reqDetail을 재시도하게 한다. 빈 body를 캐시하면 서버 복구 후에도 빈 화면 잔류.
              resolve(getBodyData({ type: 'text/html', body: '', attachments: [] }))
            })
        } else {
          const bodyData = getBodyData(self.body)
          resolve(bodyData)
        }
      })

      return promise
    },
    // getInlineImagesData: () => {
    //   const inlineImages = self.inlineFiles.map((attachment) => {
    //     return attachment.dataForJSON
    //   })
    //   return JSON.stringify(inlineImages)
    // },
  }
}

const createEmailInfoFromEmail = (
  email: JMAPEmail | ScheduleEmail | EMLEmail,
  isOutbox: boolean,
  targetMailbox: any,
  isThreadMode: boolean,
): EmailInfo | null => {
  let emailInfo: EmailInfo | null = null
  if (email instanceof JMAPEmail) {
    emailInfo = new (JMAPEmailInfo as any)(email, isOutbox, targetMailbox, isThreadMode)
  }
  if (email instanceof ScheduleEmail) {
    emailInfo = new (ScheduleEmailInfo as any)(email, isOutbox, targetMailbox, isThreadMode)
  }
  if (email instanceof EMLEmail) {
    emailInfo = new (EMLEmailInfo as any)(email, isOutbox, targetMailbox, isThreadMode)
  }

  return emailInfo
}

// const createEmailInfoFromEml = (eml) => {
//   const emailInfo = {}
//
//   return emailInfo
// }

const helper = {
  createEmailInfoFromEmail,
  // createEmailInfoFromEml,
}

export default helper
